import React from "react";
import QRCode from "react-qr-code";
import { rupees } from "../rates/rateModel";
import { studioPieceQrUrl } from "../../sales/internalQr";
import { Computed, GROUPS, Header, Layers, Line, STATUS_LABEL, byDays, koriGrams, planned } from "./costingModel";

const readStudio = (): string => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.organizationResponse?.name || "";
  } catch {
    return "";
  }
};

/** How a line was worked out, in words, for the paper copy. */
const howWorked = (l: Line): string => {
  switch (l.kind) {
    case "YARN": {
      const k = koriGrams(l);
      return `${l.khariGrams ?? "—"} g khari × ${l.koriFactor ?? "—"} = ${k ?? "—"} g kori × ₹${l.ratePerKg ?? "—"}/kg`;
    }
    case "QTY_RATE":
      return `${l.qty ?? "—"} ${l.unit || ""} × ₹${l.rate ?? "—"}`;
    case "SHARED":
      return `₹${l.sharedTotal ?? "—"} ÷ ${l.sharedSarees ?? "—"} sarees`;
    case "PERCENT_OF_MATERIAL":
      return `${l.percent ?? "—"}% of material`;
    case "LABOUR":
      if (l.method === "ACTUAL") return `Paid in Karkhana`;
      if (l.method === "PLANNED") return `${l.plannedInches ?? "—"} in × ₹${l.ratePerInch ?? "—"}/in (planned ${planned(l) ?? "—"})`;
      if (l.method === "DAYS") return `${l.days ?? "—"} days × ₹${l.dayRate ?? "—"}/day (${byDays(l) ?? "—"})`;
      return "Amount";
    default:
      return "Amount";
  }
};

/** Styles for the paper copy, used by the on-screen preview and by the print frame alike. Portrait A4. */
export const PRINT_CSS = `
.kc-print { box-sizing: border-box; width: 661px; padding: 0; color: #000; background: #fff;
  font: 11px/1.25 -apple-system, "Segoe UI", Roboto, "Noto Sans", "Noto Sans Devanagari", Arial, sans-serif; }
.kc-print * { box-sizing: border-box; }
.kc-print__head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px;
  margin-bottom: 8px; padding-bottom: 6px; border-bottom: 2px solid #000; }
.kc-print__head h1 { margin: 0; font-size: 16px; }
.kc-print__head h2 { margin: 2px 0 0; font-size: 13px; font-weight: 500; }
.kc-print__qr { display: flex; align-items: center; gap: 8px; text-align: right; font-size: 10px; }
.kc-print__qr svg { width: 56px; height: 56px; }
.kc-print table { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
.kc-print td, .kc-print th { padding: 2px 5px; border: 1px solid #999; vertical-align: top; text-align: left;
  overflow-wrap: anywhere; word-break: break-word; }
.kc-print th { background: #f0f0f0; font-weight: 600; }
.kc-print thead { display: table-header-group; }
.kc-print tr { break-inside: avoid; page-break-inside: avoid; }
.kc-print .num { text-align: right; white-space: nowrap; }
.kc-print small { color: #444; }
.kc-print__facts td:nth-child(odd) { width: 70px; color: #444; }
.kc-print__group td { background: #eee; font-weight: 600; }
.kc-print__bottom { display: flex; gap: 14px; align-items: flex-start; break-inside: avoid; page-break-inside: avoid; }
.kc-print__ladder { flex: 1; }
.kc-print__ladder .is-key td { font-weight: 700; }
.kc-print__note { margin: 4px 0 8px; }
.kc-print__sign { display: flex; gap: 40px; margin-top: 14px; break-inside: avoid; page-break-inside: avoid; }
.kc-print__sign div { flex: 1; }
.kc-print__sign span { display: block; margin-top: 24px; border-top: 1px solid #000; }
`;

/**
 * The costing on paper (portrait A4): every line, how it was worked out, the price ladder, the
 * product's QR code and signatures. Column names repeat on every page when it runs long.
 */
const CostingPrint = ({
  costing,
  lines,
  layers,
  header,
  calc,
  product,
  variants,
}: {
  costing: any;
  lines: Line[];
  layers: Layers;
  header: Header;
  calc: Computed | null;
  product?: any;
  variants?: any[];
}) => {
  if (!calc) return null;
  const t = calc.totals;
  const today = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  return (
    <div className="kc-print">
      <header className="kc-print__head">
        <div>
          <h1>{readStudio()}</h1>
          <h2>
            Saree costing · लागत पत्रक — {header.sareeNumber ? `Saree ${header.sareeNumber}` : `Estimate ${header.designNumber || ""}`}
          </h2>
        </div>
        <div className="kc-print__qr">
          <div>
            {STATUS_LABEL[costing.status] || costing.status} · v{costing.versionNo}
            <br />
            Printed {today}
            {product ? (
              <>
                <br />
                Scan to open the product
              </>
            ) : null}
          </div>
          {product?.productUniqueId ? <QRCode value={studioPieceQrUrl(product.productUniqueId)} size={128} viewBox="0 0 256 256" /> : null}
        </div>
      </header>
      <table className="kc-print__facts">
        <tbody>
          <tr>
            <td>Saree no.</td>
            <td>{header.sareeNumber || "—"}</td>
            <td>Design</td>
            <td>
              {header.designNumber || "—"} {header.designName || ""}
            </td>
            <td>Loom</td>
            <td>{header.loomCode || "—"}</td>
          </tr>
          <tr>
            <td>Product</td>
            <td colSpan={3}>
              {product ? `${product.name || ""} (${product.productUniqueId}${product.srNo ? `, ${product.srNo}` : ""})` : "Not linked"}
              {variants && variants.length ? ` · variants: ${variants.map((v: any) => v.color || v.productUniqueId).join(", ")}` : ""}
            </td>
            <td>Weight</td>
            <td>{header.weightGrams ? `${header.weightGrams} g` : "—"}</td>
          </tr>
          <tr>
            <td>Weavers</td>
            <td colSpan={3}>{header.weavers || "—"}</td>
            <td>Length</td>
            <td>{header.lengthInches ? `${header.lengthInches} in` : "—"}</td>
          </tr>
        </tbody>
      </table>

      <table className="kc-print__lines">
        <thead>
          <tr>
            <th>Cost head · मद</th>
            <th>How worked out</th>
            <th className="num">₹</th>
          </tr>
        </thead>
        <tbody>
          {GROUPS.map((g) => {
            const rows = lines.map((l, i) => ({ l, i })).filter((x) => x.l.group === g.id);
            if (!rows.length) return null;
            const total = rows.reduce((s, x) => s + (calc.amounts[x.l.id || `new-${x.i}`] || 0), 0);
            return (
              <React.Fragment key={g.id}>
                <tr className="kc-print__group">
                  <td colSpan={2}>
                    {g.title} · {g.hi}
                  </td>
                  <td className="num">{rupees(total, 0)}</td>
                </tr>
                {rows.map(({ l, i }) => {
                  const a = calc.amounts[l.id || `new-${i}`];
                  return (
                    <tr key={l.id || i}>
                      <td>
                        {l.label}
                        {l.labelHi ? <small> {l.labelHi}</small> : null}
                      </td>
                      <td>{howWorked(l)}</td>
                      <td className="num">{a === null || a === undefined ? "—" : rupees(a, 0)}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      <div className="kc-print__bottom">
      <table className="kc-print__ladder">
        <tbody>
          <tr>
            <td>Production cost · उत्पादन लागत</td>
            <td className="num">{rupees(t.production, 0)}</td>
          </tr>
          <tr>
            <td>+ Business overhead {layers.overheadPct ?? "—"}%</td>
            <td className="num">{rupees(t.overhead, 0)}</td>
          </tr>
          <tr className="is-key">
            <td>Cost price · लागत मूल्य</td>
            <td className="num">{rupees(t.costPrice, 0)}</td>
          </tr>
          <tr>
            <td>
              + Profit {layers.profitPct ?? "—"}% {layers.roundTo && layers.roundTo !== "0" ? `(rounded up to ₹${layers.roundTo})` : ""}
            </td>
            <td className="num">{rupees(t.profit, 0)}</td>
          </tr>
          <tr className="is-key">
            <td>Sale price · बिक्री मूल्य</td>
            <td className="num">{rupees(t.salePrice, 0)}</td>
          </tr>
          <tr>
            <td>− Discount {layers.discountPct ?? "—"}%</td>
            <td className="num">{rupees(t.discount, 0)}</td>
          </tr>
          <tr>
            <td>+ GST {layers.gstPct ?? "—"}%</td>
            <td className="num">{rupees(t.gst, 0)}</td>
          </tr>
          <tr className="is-key">
            <td>Price with GST</td>
            <td className="num">{rupees(t.finalPrice, 0)}</td>
          </tr>
        </tbody>
      </table>
      {costing.note ? <p className="kc-print__note" style={{ flex: 1 }}>Note: {costing.note}</p> : null}
      </div>

      <footer className="kc-print__sign">
        <div>
          Prepared by: {costing.preparedByName || "—"}
          <span />
        </div>
        <div>
          Approved by: {costing.decidedByName || "—"}
          {costing.decidedAt ? ` on ${new Date(costing.decidedAt).toLocaleDateString("en-IN")}` : ""}
          <span />
        </div>
      </footer>
    </div>
  );
};

export default CostingPrint;
